import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { clientApi, paymentApi } from '../lib/api';
import { track, trackAdsPurchaseConversion } from '../lib/analytics';
import { formatDate, formatPrice, RESERVATION_STATUS, PAYMENT_STATUS, CATEGORIES, OPTIONS } from '../lib/utils';
import { ArrowLeft, Calendar, User, MapPin, Clock, Download, CreditCard, Check, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Logo from '../components/Logo';

export default function ReservationDetail() {
  const { reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { config } = useConfig();
  const [reservation, setReservation] = useState(null);
  const [shoppingList, setShoppingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    loadReservation();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [reservationId, searchParams]);

  const loadReservation = async () => {
    try {
      const [resResponse, listResponse] = await Promise.all([
        clientApi.getReservation(reservationId),
        clientApi.getShoppingList(reservationId).catch(() => ({ data: [] }))
      ]);
      setReservation(resResponse.data);
      setShoppingList(listResponse.data);
    } catch (error) {
      console.error('Error loading reservation:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Vérification du paiement en cours...');
      return;
    }

    try {
      const response = await paymentApi.getCheckoutStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        track('purchase', {
          transaction_id: reservationId,
          value: reservation?.total_amount || 0,
          currency: 'EUR',
        });
        // Conversion Google Ads : uniquement sur paiement réellement finalisé.
        // Montant réel payé + transaction_id unique pour éviter les doublons.
        trackAdsPurchaseConversion({
          value: reservation?.total_amount || 0,
          transaction_id: reservationId,
        });
        toast.success('Paiement confirmé !');
        loadReservation();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
    }
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      track('begin_checkout', {
        transaction_id: reservationId,
        value: reservation?.total_amount || 0,
        currency: 'EUR',
      });
      const response = await paymentApi.createCheckout(
        reservationId,
        window.location.origin
      );
      window.location.href = response.data.url;
    } catch (error) {
      track('payment_error', { transaction_id: reservationId });
      toast.error('Erreur lors de la création du paiement');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#5C6A61] mb-4">Réservation non trouvée</p>
          <Link to="/client" className="btn-primary">Retour au tableau de bord</Link>
        </div>
      </div>
    );
  }

  const statusInfo = RESERVATION_STATUS[reservation.status];
  const paymentInfo = PAYMENT_STATUS[reservation.payment_status];

  // Group shopping list by category
  const groupedShoppingList = shoppingList.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/client" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C]">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Retour</span>
            </Link>
            <Logo />
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="card-default mb-6">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C]">
                  Réservation #{reservation.reservation_id.slice(-8)}
                </h1>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
                <Badge className={paymentInfo?.color}>{paymentInfo?.label}</Badge>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-semibold text-[#2A312C]">{formatPrice(reservation.total_amount)}</p>
              {reservation.promo_code && (
                <p className="text-sm text-[#C86B51]" data-testid="detail-promo-badge">
                  {reservation.promo_label} -{reservation.promo_percent} % appliquée
                </p>
              )}
              {reservation.urssaf_advance_enabled && (
                <p className="text-sm text-[#A5BBA8]">
                  Reste à charge estimé: {formatPrice(reservation.total_amount - reservation.estimated_urssaf_credit)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details */}
            <div className="card-default">
              <h2 className="font-semibold text-[#2A312C] mb-4">Détails de l'intervention</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#A5BBA8]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C6A61]">Date</p>
                    <p className="font-medium text-[#2A312C]">{formatDate(reservation.date)}</p>
                  </div>
                </div>
                {reservation.start_time && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#A5BBA8]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5C6A61]">Heure</p>
                      <p className="font-medium text-[#2A312C]">{reservation.start_time}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#A5BBA8]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5C6A61]">Formule</p>
                    <p className="font-medium text-[#2A312C]">{reservation.formula} • {reservation.household_size} pers.</p>
                  </div>
                </div>
              </div>
              
              {/* Options */}
              {Object.values(reservation.options || {}).some(v => v) && (
                <div className="mt-4 pt-4 border-t border-[#E3DED5]">
                  <p className="text-sm text-[#5C6A61] mb-2">Options</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(reservation.options || {}).map(([key, value]) => (
                      value && OPTIONS[key] && (
                        <span key={key} className="px-3 py-1 bg-[#C86B51]/10 text-[#C86B51] rounded-full text-sm">
                          {OPTIONS[key].name}
                        </span>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Supplément déplacement (zone géographique) */}
              {reservation.travel_zone && Number(reservation.travel_zone.surcharge || 0) > 0 && (
                <div className="mt-4 pt-4 border-t border-[#E3DED5]" data-testid="detail-travel-surcharge">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5C6A61]">{reservation.travel_zone.label || `Supplément déplacement — ${reservation.travel_zone.sector_name}`}</span>
                    <span className="font-medium text-[#2A312C]">+{formatPrice(reservation.travel_zone.surcharge)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Shopping List */}
            {reservation.payment_status === 'paye' && shoppingList.length > 0 && (
              <div className="card-default">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-semibold text-[#2A312C]">Liste de courses</h2>
                  <a
                    href={clientApi.downloadShoppingListPdf(reservationId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-2"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger PDF
                  </a>
                </div>
                
                <div className="space-y-4">
                  {Object.entries(groupedShoppingList).map(([category, items]) => (
                    <div key={category}>
                      <h3 className="text-sm font-medium text-[#5C6A61] uppercase tracking-wide mb-2">
                        {category.replace('_', ' ')}
                      </h3>
                      <div className="space-y-1">
                        {items.map((item, index) => (
                          <div key={index} className="flex justify-between items-center py-1 border-b border-[#E3DED5] last:border-0">
                            <span className="text-[#2A312C]">{item.ingredient_name}</span>
                            <span className="text-[#5C6A61]">{item.quantity.toFixed(1)} {item.unit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-[#F9F6F0] rounded-xl">
                  <h3 className="font-medium text-[#2A312C] mb-2">Consignes</h3>
                  <ul className="text-sm text-[#5C6A61] space-y-1">
                    <li>• Légumes frais de préférence, surgelés ou en conserve acceptés</li>
                    <li>• Champignons frais</li>
                    <li>• Crevettes décortiquées</li>
                    <li>• Poisson sans arêtes</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment */}
            {reservation.payment_status === 'en_attente' && (
              <div className="card-default">
                <h2 className="font-semibold text-[#2A312C] mb-4">Paiement</h2>
                <div className="space-y-3 mb-4">
                  {reservation.promo_code && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#5C6A61]">Tarif initial</span>
                        <span className="text-[#5C6A61]">{formatPrice(reservation.amount_before_discount)}</span>
                      </div>
                      <div className="flex justify-between text-sm" data-testid="detail-promo-discount">
                        <span className="text-[#C86B51]">{reservation.promo_label} {reservation.promo_code} (-{reservation.promo_percent} %)</span>
                        <span className="text-[#C86B51]">-{formatPrice(reservation.discount_amount)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#5C6A61]">{reservation.promo_code ? 'Montant à payer' : 'Total'}</span>
                    <span className="font-semibold text-[#2A312C]">{formatPrice(reservation.total_amount)}</span>
                  </div>
                  {reservation.urssaf_advance_enabled && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#A5BBA8]">Crédit d'impôt estimé</span>
                        <span className="text-[#A5BBA8]">-{formatPrice(reservation.estimated_urssaf_credit)}</span>
                      </div>
                      <div className="flex justify-between border-t border-[#E3DED5] pt-2">
                        <span className="text-[#5C6A61]">Reste à charge</span>
                        <span className="font-semibold text-[#2A312C]">
                          {formatPrice(reservation.total_amount - reservation.estimated_urssaf_credit)}
                        </span>
                      </div>
                      <p className="text-xs text-[#5C6A61]">* Montant estimatif sous réserve d'éligibilité</p>
                    </>
                  )}
                </div>
                <button
                  onClick={handlePayment}
                  disabled={paymentLoading}
                  className="btn-secondary w-full"
                  data-testid="pay-now-btn"
                >
                  {paymentLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-5 h-5 mr-2" />
                  )}
                  Payer maintenant
                </button>
              </div>
            )}

            {/* Confirmation */}
            {reservation.payment_status === 'paye' && (
              <div className="card-default bg-[#A5BBA8]/10 border-[#A5BBA8]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#A5BBA8] flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="font-semibold text-[#2A312C]">Réservation confirmée</h2>
                </div>
                <p className="text-sm text-[#5C6A61]">
                  Votre réservation est confirmée. N'oubliez pas de préparer vos courses avant l'intervention.
                </p>
              </div>
            )}

            {/* Notes */}
            {reservation.notes && (
              <div className="card-default">
                <h2 className="font-semibold text-[#2A312C] mb-2">Notes</h2>
                <p className="text-[#5C6A61]">{reservation.notes}</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
